/**
 * Pure data model behind the race graph (RaceTimeline.tsx): who goes in which
 * row, where each racer tapped and where they were counted, and the
 * piecewise time scale. No React in here, so it is easy to test.
 *
 * Timing words used throughout:
 * - counted = `offsetMs`, the contest time that decided the ranking.
 * - tapped  = counted minus the sum of applied effects (card head starts,
 *   the no-emoji penalty). Derived from the sum invariant instead of
 *   `effectiveOffsetMs` on purpose: it can never leak a latency adjustment,
 *   which the owner asked to keep off the graph.
 */

import type { RaceAppliedEffect, RaceData, RaceParticipant } from '../../../api/types';
import { isNoEmojiPenaltyEffect, noEmojiPenaltyMsOf } from '../../../data/noEmojiPenalty';

/** The contest window. Rows past generation time + this are not race rows. */
export const CONTEST_WINDOW_MS = 45_000;
/** Pre-unlock taps further out than this were never charted. */
export const EARLY_FLOOR_MS = -4_500;

export type RacerKind = 'winner' | 'racer' | 'late' | 'hidden' | 'early' | 'blocked';

export interface Shift {
  /** Display name: the card name, or "No-emoji penalty". */
  label: string;
  deltaMs: number;
  /** True for the no-emoji penalty, false for respect cards. */
  isPenalty: boolean;
}

export interface Racer {
  key: string;
  username: string;
  emoji: string | null;
  kind: RacerKind;
  isWinner: boolean;
  /** Contest time from unlock, null when a card hid it. */
  countedMs: number | null;
  /** Where they actually tapped, before effects. Equals countedMs when nothing moved them. */
  tappedMs: number | null;
  /** Non-zero effects, in the order they were applied. */
  shifts: Shift[];
  /** The no-emoji penalty was due but waived (raced the day before). */
  penaltyWaived: boolean;
  /** countedMs minus the winner's countedMs. Null for the winner or when unknown. */
  gapMs: number | null;
  blockedBy: string[];
  blockedByCard: string | null;
}

export interface RaceModel {
  /** Winner first, then racers by counted time, then hidden times, then late arrivals. */
  main: Racer[];
  /** Early taps and Gridlock-blocked attempts. */
  secondary: Racer[];
  winner: Racer | null;
  /** Fastest timed racer after the winner (late arrivals excluded). */
  runnerUp: Racer | null;
  /** How far ahead the winner finished, when both times are known. */
  marginMs: number | null;
}

// ---- small helpers ----

function prettifySlug(slug: string): string {
  return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function cleanEmoji(emoji?: string | null): string | null {
  const t = typeof emoji === 'string' ? emoji.trim() : '';
  return t ? t : null;
}

function isNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** Effects on a row. Prefers the per-effect breakdown; legacy rows only carry
 *  the aggregate appliedAdvantageMs/CardSlug pair. */
function effectsOf(p: RaceParticipant): RaceAppliedEffect[] {
  if (Array.isArray(p.appliedEffects) && p.appliedEffects.length > 0) return p.appliedEffects;
  if (isNum(p.appliedAdvantageMs) && p.appliedAdvantageMs !== 0) {
    return [
      {
        cardSlug: p.appliedAdvantageCardSlug ?? 'card',
        cardName: null,
        deltaMs: p.appliedAdvantageMs,
        reason: 'claim_time_advantage',
      },
    ];
  }
  return [];
}

function shiftsOf(p: RaceParticipant): { shifts: Shift[]; penaltyWaived: boolean } {
  const effects = effectsOf(p);
  const shifts: Shift[] = effects
    .filter((e) => isNum(e.deltaMs) && e.deltaMs !== 0)
    .map((e) =>
      isNoEmojiPenaltyEffect(e)
        ? { label: 'No-emoji penalty', deltaMs: e.deltaMs, isPenalty: true }
        : {
            label: (e.cardName && e.cardName.trim()) || prettifySlug(e.cardSlug || 'card'),
            deltaMs: e.deltaMs,
            isPenalty: false,
          },
    );
  // A waived penalty is still written to the row, with deltaMs 0.
  const penaltyWaived = effects.some(isNoEmojiPenaltyEffect) && noEmojiPenaltyMsOf(effects) === 0;
  return { shifts, penaltyWaived };
}

function toRacer(p: RaceParticipant, kind: RacerKind, isWinner: boolean): Racer {
  const hidden = Boolean(p.displayHidden) || !isNum(p.offsetMs);
  const { shifts, penaltyWaived } = hidden ? { shifts: [], penaltyWaived: false } : shiftsOf(p);
  const countedMs = hidden ? null : p.offsetMs;
  const net = shifts.reduce((s, x) => s + x.deltaMs, 0);
  return {
    key: `${kind}:${p.username}`,
    username: p.username,
    emoji: cleanEmoji(p.userEmoji),
    kind,
    isWinner,
    countedMs,
    tappedMs: countedMs === null ? null : countedMs - net,
    shifts,
    penaltyWaived,
    gapMs: null,
    blockedBy: (p.blockedByUsernames ?? []).filter((u) => typeof u === 'string' && u.length > 0),
    blockedByCard: p.blockedByCardSlug ?? null,
  };
}

/** Builds the rows. Returns null when there is no race to draw (nobody besides the winner). */
export function buildRaceModel(race: RaceData, generationTimeMs?: number): RaceModel | null {
  const authorName = race.author?.username || null;
  const cutoff = isNum(generationTimeMs) ? generationTimeMs + CONTEST_WINDOW_MS : Infinity;

  const winner = authorName ? toRacer(race.author, 'winner', true) : null;
  const racers: Racer[] = [];
  const hidden: Racer[] = [];
  const late: Racer[] = [];
  const early: Racer[] = [];
  const blocked: Racer[] = [];
  const seen = new Set<string>();

  for (const p of race.participants ?? []) {
    if (!p || !p.username) continue;
    if (p.outcome === 'blocked') {
      blocked.push(toRacer(p, 'blocked', false));
      continue;
    }
    if (p.outcome === 'early') {
      const r = toRacer(p, 'early', false);
      // Hidden early rows stay (no time); timed ones only inside the charted band.
      if (r.countedMs === null || (r.countedMs < 0 && r.countedMs >= EARLY_FLOOR_MS)) early.push(r);
      continue;
    }
    // The author is usually not repeated in participants, but older payloads
    // and tests do repeat it: the author row always wins.
    if (p.username === authorName || seen.has(p.username)) continue;
    seen.add(p.username);
    if (isNum(p.offsetMs) && p.offsetMs > cutoff) continue;
    if (p.displayHidden || !isNum(p.offsetMs)) hidden.push(toRacer(p, 'hidden', false));
    else if (p.abortReason === 'late_arrival') late.push(toRacer(p, 'late', false));
    else racers.push(toRacer(p, 'racer', false));
  }

  const byCounted = (a: Racer, b: Racer) => (a.countedMs ?? 0) - (b.countedMs ?? 0);
  racers.sort(byCounted);
  late.sort(byCounted);
  early.sort(byCounted);

  const main = [...(winner ? [winner] : []), ...racers, ...hidden, ...late];
  const secondary = [...early, ...blocked];
  if (main.length + secondary.length === 0) return null;
  if (main.length <= 1 && secondary.length === 0) return null;

  const winnerMs = winner?.countedMs ?? null;
  if (winnerMs !== null) {
    for (const r of [...racers, ...late]) {
      if (r.countedMs !== null) r.gapMs = r.countedMs - winnerMs;
    }
  }
  const runnerUp = racers.find((r) => r.countedMs !== null) ?? null;
  const marginMs =
    winnerMs !== null && runnerUp && runnerUp.countedMs !== null ? runnerUp.countedMs - winnerMs : null;

  return { main, secondary, winner, runnerUp, marginMs };
}

/** Every point a racer occupies on the time axis: the tap, each effect step, the counted time. */
export function pathOf(r: Racer): number[] {
  if (r.tappedMs === null || r.countedMs === null) return [];
  const pts = [r.tappedMs];
  let at = r.tappedMs;
  for (const s of r.shifts) {
    at += s.deltaMs;
    pts.push(at);
  }
  // Guard against payloads where the effects don't add up exactly.
  pts[pts.length - 1] = r.countedMs;
  return pts;
}

// ---- the time scale ----

export interface Tick {
  ms: number;
  /** 0..1 across the plot width. */
  frac: number;
  label: string;
}

export interface TimeScale {
  /** Maps ms-from-unlock to 0..1 across the plot. */
  x: (ms: number) => number;
  /** Where unlock (0ms) sits: 0, or the right edge of the pre-unlock zone. */
  zeroFrac: number;
  /** Start of the squeezed tail, null when the whole race fits the linear part. */
  breakFrac: number | null;
  /** End of the linear part, in ms. */
  linearEndMs: number;
  ticks: Tick[];
}

/** Values the linear part can end on. Each divides into 2 or 3 clean ticks. */
const LINEAR_ENDS = [300, 400, 500, 600, 750, 1000, 1500, 2000, 3000, 4000, 5000, 6000, 10_000, 15_000, 20_000, 30_000, 45_000, 60_000];
const TICK_STEPS = [50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10_000, 15_000, 20_000, 30_000];
const TAIL_TICKS = [1500, 2000, 3000, 5000, 10_000, 15_000, 20_000, 30_000, 45_000, 60_000, 90_000, 120_000, 300_000];
const PRE_ENDS = [100, 250, 500, 1000, 2000, 4500];

function niceAtLeast(v: number, list: number[]): number {
  for (const n of list) if (n >= v) return n;
  return Math.ceil(v / 60_000) * 60_000;
}

export function formatTick(ms: number): string {
  if (ms === 0) return '0s';
  return `${+(ms / 1000).toFixed(2)}s`;
}

/**
 * A piecewise axis built for how races actually look: taps cluster in the
 * first few hundred ms, stragglers trickle in seconds later.
 *
 * - [0, linearEnd]: linear and honest. linearEnd is the smallest clean value
 *   that covers the winner with room to spare and everyone who finished
 *   within ~400ms of them, so the decisive cluster gets most of the width.
 * - (linearEnd, last]: a log-squeezed tail on a shaded band, so a +6s
 *   straggler still shows up without flattening the cluster.
 * - [earliest, 0): a narrow pre-unlock zone, only when someone tapped early
 *   (or a head start pulled a racer before unlock).
 *
 * `plotPx` is the measured plot width; it only decides which tick labels fit.
 */
export function buildScale(model: RaceModel, plotPx: number): TimeScale {
  const timed = [...model.main, ...model.secondary].filter((r) => r.countedMs !== null && r.kind !== 'blocked');
  const points = timed.flatMap((r) => (r.kind === 'early' ? [r.countedMs as number] : pathOf(r)));
  const winnerMs = model.winner?.countedMs ?? null;

  const minPt = points.length ? Math.min(...points) : 0;
  const maxPt = points.length ? Math.max(...points) : 0;

  // The decisive cluster: real racers (late arrivals are not contenders)
  // within 400ms of the winner, plus the winner with a margin.
  const contenders = model.main
    .filter((r) => r.kind === 'winner' || r.kind === 'racer')
    .flatMap((r) => pathOf(r))
    .filter((v) => v >= 0);
  const anchor = winnerMs !== null && winnerMs >= 0 ? winnerMs : contenders.length ? Math.min(...contenders) : 0;
  const cluster = contenders.filter((v) => v <= anchor + 400);
  let linearEnd = niceAtLeast(Math.max(anchor * 1.6, (cluster.length ? Math.max(...cluster) : 0) * 1.08, 250), LINEAR_ENDS);

  // Everything fits comfortably: no tail, just stretch the linear part.
  const hasTail = maxPt > linearEnd * 1.2;
  if (!hasTail) linearEnd = niceAtLeast(Math.max(linearEnd, maxPt * 1.06), LINEAR_ENDS);
  const tailEndMs = hasTail ? maxPt * 1.05 : linearEnd;

  const preStartMs = minPt < 0 ? -niceAtLeast(-minPt * 1.15, PRE_ENDS) : 0;
  // Fixed shares, so a resize never moves a marker: only tick density follows the width.
  const zeroFrac = preStartMs < 0 ? 0.12 : 0;
  const tailFrac = hasTail ? 0.24 : 0;
  const linEndFrac = 1 - tailFrac;

  const x = (ms: number): number => {
    let f: number;
    if (ms < 0) f = preStartMs < 0 ? zeroFrac * (1 - ms / preStartMs) : 0;
    else if (ms <= linearEnd) f = zeroFrac + (linEndFrac - zeroFrac) * (ms / linearEnd);
    else if (hasTail) f = linEndFrac + tailFrac * (Math.log(ms / linearEnd) / Math.log(tailEndMs / linearEnd));
    else f = 1;
    return Math.max(0, Math.min(1, f));
  };

  // Tick labels must not touch: estimate each label's width from its length
  // (the page font is monospace, ~5.8px a character at the label size).
  const zeroLabel = zeroFrac > 0 ? 'unlock' : formatTick(0);
  const labelPx = (label: string) => label.length * 5.8;
  const fits = (a: Tick, b: Tick, pad = 8) =>
    Math.abs(a.frac - b.frac) * plotPx >= (labelPx(a.label) + labelPx(b.label)) / 2 + pad;
  const linearTicks = (step: number): Tick[] => {
    const out: Tick[] = [];
    for (let ms = 0; ms <= linearEnd + 1e-6; ms += step) {
      out.push({ ms, frac: x(ms), label: ms === 0 ? zeroLabel : formatTick(ms) });
    }
    return out;
  };
  let ticks: Tick[] = [{ ms: 0, frac: x(0), label: zeroLabel }];
  for (const step of TICK_STEPS) {
    if (linearEnd % step !== 0 || linearEnd / step > 6) continue;
    const candidate = linearTicks(step);
    if (candidate.every((t, i) => i === 0 || fits(candidate[i - 1], t))) {
      ticks = candidate;
      break;
    }
  }
  if (ticks.length === 1) {
    const end: Tick = { ms: linearEnd, frac: x(linearEnd), label: formatTick(linearEnd) };
    if (fits(ticks[0], end)) ticks.push(end);
  }

  // Tail ticks: largest clean values first, while they don't crowd a neighbour.
  if (hasTail) {
    const candidates = TAIL_TICKS.filter((v) => v > linearEnd * 1.3 && v <= tailEndMs).reverse();
    for (const v of candidates) {
      const t: Tick = { ms: v, frac: x(v), label: formatTick(v) };
      // Room to the right edge: the plot is inset, so half a label may hang over.
      if ((1 - t.frac) * plotPx + 10 < labelPx(t.label) / 2) continue;
      // A little more air in the tail, where the spacing is uneven anyway.
      if (ticks.every((o) => fits(o, t, 14))) ticks.push(t);
    }
    ticks.sort((a, b) => a.ms - b.ms);
  }

  return { x, zeroFrac, breakFrac: hasTail ? linEndFrac : null, linearEndMs: linearEnd, ticks };
}

// ---- text ----

const MINUS = '−';

/** "+0.238s", "−0.080s", "+12.40s", "+1m 04s". */
export function formatSeconds(ms: number, signed = true): string {
  const sign = ms < 0 ? MINUS : signed ? '+' : '';
  const a = Math.abs(ms);
  if (a < 10_000) return `${sign}${(a / 1000).toFixed(3)}s`;
  if (a < 60_000) return `${sign}${(a / 1000).toFixed(2)}s`;
  const s = Math.round(a / 1000);
  return `${sign}${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

/** Signed gaps and effect sizes: "+37ms", "−120ms", "+2.96s", "+12.4s". */
export function formatGap(ms: number): string {
  const sign = ms < 0 ? MINUS : '+';
  const a = Math.abs(Math.round(ms));
  if (a < 1000) return `${sign}${a}ms`;
  if (a < 10_000) return `${sign}${(a / 1000).toFixed(2)}s`;
  if (a < 60_000) return `${sign}${(a / 1000).toFixed(1)}s`;
  const s = Math.round(a / 1000);
  return `${sign}${Math.floor(s / 60)}m ${s % 60}s`;
}

/** Unsigned duration: "37ms", "2.96s". */
export function formatDuration(ms: number): string {
  return formatGap(Math.abs(ms)).slice(1);
}

export function identityOf(r: Pick<Racer, 'emoji' | 'username'>): string {
  return r.emoji ? `${r.emoji} ${r.username}` : r.username;
}

export function blockerText(r: Racer): string {
  return r.blockedBy.length > 0 ? `by ${r.blockedBy.join(', ')}` : 'blocked';
}

export function blockCardName(r: Racer): string {
  return r.blockedByCard ? prettifySlug(r.blockedByCard) : 'a card';
}

/** One line a screen reader announces for a racer's button. */
export function ariaSummary(r: Racer): string {
  const who = identityOf(r);
  const parts: string[] = [who];
  if (r.kind === 'blocked') {
    parts.push(`blocked ${blockerText(r)}${r.blockedByCard ? ` with ${blockCardName(r)}` : ''}, never raced`);
    return parts.join(', ');
  }
  if (r.countedMs === null) {
    parts.push(r.isWinner ? 'won' : r.kind === 'early' ? 'tapped before unlock' : 'lost', 'time hidden');
    return parts.join(', ');
  }
  if (r.kind === 'early') {
    parts.push(`tapped ${formatDuration(r.countedMs)} before unlock, did not count`);
    return parts.join(', ');
  }
  if (r.isWinner) parts.push('won');
  else if (r.kind === 'late') parts.push('late, arrived after the day was claimed');
  else parts.push('lost');
  if (r.shifts.length > 0 && r.tappedMs !== null) {
    const steps = r.shifts.map((s) => `${s.isPenalty ? 'no-emoji penalty' : s.label} ${formatGap(s.deltaMs)}`);
    parts.push(`tapped ${formatSeconds(r.tappedMs)}, ${steps.join(', ')}, counted ${formatSeconds(r.countedMs)}`);
  } else {
    parts.push(`tapped ${formatSeconds(r.countedMs)} after unlock`);
  }
  if (r.gapMs !== null) parts.push(`${formatDuration(r.gapMs)} ${r.gapMs >= 0 ? 'behind' : 'ahead of'} the winner`);
  if (r.penaltyWaived) parts.push('no-emoji penalty waived');
  return parts.join(', ');
}
